import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLike } from '@apollo/client';
import {
  Widget,
  WidgetUpdateInputObject,
  WidgetsQuery,
  WidgetsKindEnum,
  useWidgetBulkUpdateMutation,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import { Modals } from '@lib/hooks/useModalManagement';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';
import prepareWidgetFromFormValues from '../utils/prepareWidgetFromFormValues';
import { getWidgetSizeConstraints } from '../constants';
import {
  GridLayoutItem,
  LocalWidget,
  widgetToLocalWidget,
  localWidgetToUpdateInput,
  getPositionMapFromGrid,
  getGridItemMap,
} from '../utils/widgetConverters';
import {
  processAnnouncementAttachments,
  processExternalLinkAttachments,
} from '../utils/processWidgetAttachments';

// Re-export types for external use
export type { GridLayoutItem, LocalWidget } from '../utils/widgetConverters';

/**
 * Props for the useWidgetEditor hook.
 */
interface UseWidgetEditorProps {
  /** ID of the home page to update widgets for */
  homePageId: string;
  /** Current widgets from API */
  widgets: Widget[];
  /** Callback to exit edit mode */
  onExitEditMode: () => void;
  /** Callback to refetch widgets after save */
  refetchWidgets: () => Promise<{
    data: WidgetsQuery | undefined;
    error?: ErrorLike;
  }>;
  /** Function to open a modal by name */
  openModal: (name: string) => void;
  /** Function to close a modal by name */
  closeModal: (name: string) => void;
}

/**
 * Return type for the useWidgetEditor hook.
 */
interface UseWidgetEditorReturn {
  localWidgets: LocalWidget[];
  localGrid: GridLayoutItem[];
  setLocalWidgets: React.Dispatch<React.SetStateAction<LocalWidget[]>>;
  setLocalGrid: React.Dispatch<React.SetStateAction<GridLayoutItem[]>>;
  saveLoading: boolean;
  // Widget modal state (data only, open/close managed by useModalManagement)
  editingWidget: LocalWidget | null;
  widgetModalLoading: boolean;
  // Widget modal handlers
  handleOpenWidgetModal: () => void;
  handleCloseWidgetModal: () => void;
  handleEditWidget: (widgetId: string) => void;
  handleSubmitWidgetModal: (values: Values) => void;
  // Widget actions
  handleRemoveWidget: (widgetId: string) => void;
  // Layout
  initializeLocalWidgets: () => void;
  updateLayout: (layouts: GridLayoutItem[]) => void;
  // Edit mode actions
  handleCancelEdit: () => void;
  handleSaveView: () => Promise<void>;
}

/**
 * Hook for managing widget editing state and operations.
 *
 * Handles:
 * - Local widget state during edit mode
 * - Widget modal (add/edit) operations
 * - Grid layout management
 * - Saving changes to API via bulk update
 *
 * @example
 * ```tsx
 * const {
 *   localWidgets,
 *   localGrid,
 *   handleOpenWidgetModal,
 *   handleSaveView,
 * } = useWidgetEditor({
 *   homePageId,
 *   widgets,
 *   onExitEditMode,
 *   refetchWidgets,
 * });
 * ```
 */
export default function useWidgetEditor({
  homePageId,
  widgets,
  onExitEditMode,
  refetchWidgets,
  openModal,
  closeModal,
}: UseWidgetEditorProps): UseWidgetEditorReturn {
  const { t } = useTranslation();
  const [localWidgets, setLocalWidgets] = useState<LocalWidget[]>([]);
  const [localGrid, setLocalGrid] = useState<GridLayoutItem[]>([]);
  // Widget modal data state (open/close managed by useModalManagement in HomeContainer)
  const [editingWidget, setEditingWidget] = useState<LocalWidget | null>(null);
  const [widgetModalLoading, setWidgetModalLoading] = useState(false);

  const [widgetBulkUpdate, { loading: saveLoading }] =
    useWidgetBulkUpdateMutation();

  // Initialize local state from props - only called when entering edit mode
  const initializeLocalWidgets = useCallback(() => {
    setLocalWidgets(widgets.map(widgetToLocalWidget));
    // Clear local grid - it will be re-initialized by Widgets component from widget grid data
    setLocalGrid([]);
  }, [widgets]);

  // Add new widget from form values - store only raw form values (single source of truth)
  const addWidget = useCallback((formValues: Values) => {
    // Extract minimal info needed for grid constraints
    const widgetData = prepareWidgetFromFormValues(formValues);
    if (!widgetData) return;

    const tempId = `temp-${Date.now()}`;
    const widgetKind = widgetData.kind || '';
    const newWidget: LocalWidget = {
      tempId,
      kind: widgetKind,
      // Only store widgetTemplateId for new widgets (required for API)
      widgetTemplateId: widgetData.widgetTemplateId,
      // Store only raw form values - widget data will be derived when needed
      formValues,
    };

    setLocalWidgets((prev) => [...prev, newWidget]);

    // Get size constraints for this widget type
    const constraints = getWidgetSizeConstraints(widgetKind);

    // Add grid layout item for new widget with size constraints
    // Place new widget at the bottom of the grid (after all existing widgets)
    setLocalGrid((prev) => {
      // Calculate the maximum y + h to find the bottom of the grid
      const maxYBottom = prev.reduce((max, item) => {
        const itemBottom = item.y + item.h;
        return itemBottom > max ? itemBottom : max;
      }, 0);

      return [
        ...prev,
        {
          i: tempId,
          x: 0,
          y: maxYBottom, // Place at the bottom
          w: constraints.defaultW,
          h: constraints.defaultH,
          minW: constraints.minW,
          minH: constraints.minH,
          maxW: constraints.maxW,
          maxH: constraints.maxH,
        },
      ];
    });
  }, []);

  // Update layout from react-grid-layout - merge position/size with preserved constraints
  // react-grid-layout's onLayoutChange returns only {i, x, y, w, h} without constraint properties
  // We must preserve minW, minH, maxW, maxH to prevent widgets from resizing unexpectedly
  const updateLayout = useCallback(
    (layouts: GridLayoutItem[]) => {
      setLocalGrid((prevGrid) => {
        // Create a map of previous grid items for quick lookup of constraints
        const prevGridMap = new Map(prevGrid.map((item) => [item.i, item]));

        // Merge each layout item: position/size from RGL + constraints from previous state or widget kind
        return layouts.map((layout) => {
          // Try to get constraints from previous grid state
          const prevItem = prevGridMap.get(layout.i);

          if (prevItem) {
            // Preserve constraints from previous state while updating position/size
            return {
              ...layout,
              minW: prevItem.minW,
              minH: prevItem.minH,
              maxW: prevItem.maxW,
              maxH: prevItem.maxH,
            };
          }

          // Fallback: derive constraints from widget kind
          // This handles widgets that appear in RGL layout but not in previous grid
          const widget = localWidgets.find(
            (w) => w.id === layout.i || w.tempId === layout.i,
          );

          if (widget) {
            const constraints = getWidgetSizeConstraints(widget.kind);
            return {
              ...layout,
              minW: constraints.minW,
              minH: constraints.minH,
              maxW: constraints.maxW,
              maxH: constraints.maxH,
            };
          }

          // If no widget found, return layout as-is (edge case)
          return layout;
        });
      });
    },
    [localWidgets],
  );

  // Open widget modal for adding new widget
  const handleOpenWidgetModal = useCallback(() => {
    setEditingWidget(null);
    openModal(Modals.HomeWidgetModal);
  }, [openModal]);

  // Close widget modal
  const handleCloseWidgetModal = useCallback(() => {
    closeModal(Modals.HomeWidgetModal);
    setEditingWidget(null);
  }, [closeModal]);

  // Edit widget - open modal with widget data
  const handleEditWidget = useCallback(
    (widgetId: string) => {
      const widget = localWidgets.find(
        (w) => w.id === widgetId || w.tempId === widgetId,
      );
      if (widget) {
        setEditingWidget(widget);
        openModal(Modals.HomeWidgetModal);
      }
    },
    [localWidgets, openModal],
  );

  // Update widget from form values - store only raw form values (single source of truth)
  const updateWidget = useCallback((widgetId: string, formValues: Values) => {
    setLocalWidgets((prev) =>
      prev.map((widget) => {
        const currentId = widget.id || widget.tempId;
        if (currentId === widgetId) {
          return {
            ...widget,
            // Store only raw form values - widget data will be derived when needed
            formValues,
          };
        }
        return widget;
      }),
    );
  }, []);

  // Remove widget - mark as destroyed for existing widgets, remove from array for temp widgets
  const handleRemoveWidget = useCallback((widgetId: string) => {
    setLocalWidgets((prev) => {
      // Check if it's a temp widget (new, not saved yet)
      const widget = prev.find(
        (w) => w.id === widgetId || w.tempId === widgetId,
      );
      if (!widget) return prev;

      // If it's a temp widget, remove it completely
      if (widget.tempId && !widget.id) {
        return prev.filter((w) => w.tempId !== widgetId);
      }

      // If it's an existing widget, mark it as destroyed
      return prev.map((w) => {
        if (w.id === widgetId) {
          return { ...w, isDestroyed: true };
        }
        return w;
      });
    });

    // Also remove from grid if it's a temp widget
    setLocalGrid((prev) => prev.filter((item) => item.i !== widgetId));
  }, []);

  /**
   * Saves all widget changes to the API via bulk update.
   * Processes form values for edited widgets and handles attachment changes.
   *
   * @returns Promise<boolean> - true on success, false on failure
   */
  const saveChanges = useCallback(async (): Promise<boolean> => {
    // Use utility functions for position and grid mapping
    const positionMap = getPositionMapFromGrid(localGrid);
    const gridMap = getGridItemMap(localGrid);

    // Prepare widget updates with positions and grid data
    // For widgets with formValues, process them now with proper attachment handling
    const widgetAttributes: WidgetUpdateInputObject[] = localWidgets.map(
      (widget) => {
        const widgetId = widget.id || widget.tempId || '';
        const position = positionMap.get(widgetId) ?? 0;
        const gridItem = gridMap.get(widgetId);

        // If widget has formValues (was edited), process them now
        if (widget.formValues) {
          // Get original buttons for QuickCreation widgets
          // Cast through unknown to handle GraphQL __typename property
          const originalButtons =
            (widget.originalWidget?.fields?.buttons as unknown as Record<
              string,
              { enabled: boolean; updatedAt: string }
            >) ||
            (widget.fields?.buttons as unknown as Record<
              string,
              { enabled: boolean; updatedAt: string }
            >);

          // Extract widget data from form values
          const processedWidget = prepareWidgetFromFormValues(
            widget.formValues,
            { originalButtons },
          );

          if (processedWidget) {
            // Process attachments based on widget type
            let processedAttachments: unknown[] | undefined;
            if (processedWidget.kind === WidgetsKindEnum.Announcement) {
              processedAttachments = processAnnouncementAttachments(
                widget.formValues,
                widget.originalWidget,
              );
            } else if (processedWidget.kind === WidgetsKindEnum.ExternalLink) {
              processedAttachments = processExternalLinkAttachments(
                widget.formValues,
              );
            }

            // Merge processed data back into widget for mutation
            const updatedWidget: LocalWidget = {
              ...widget,
              title: processedWidget.title,
              description: processedWidget.description,
              fields: processedWidget.fields as Record<string, unknown>,
              attachmentsAttached: processedAttachments,
            };
            // Pass wasEdited=true for widgets with formValues
            return localWidgetToUpdateInput(
              updatedWidget,
              position,
              gridItem,
              true,
            );
          }
        }

        // No formValues means widget was not edited - pass wasEdited=false
        return localWidgetToUpdateInput(widget, position, gridItem, false);
      },
    );

    // Sort by position to ensure consistent order in the payload
    const sortedAttributes = [...widgetAttributes].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0),
    );

    try {
      await widgetBulkUpdate({
        variables: {
          homePageId,
          attributes: removeTypenameDeep(sortedAttributes),
        },
      });

      return true;
    } catch (error) {
      toast({ content: getErrorMessage(error as ErrorLike, t) });
      return false;
    }
  }, [localWidgets, localGrid, homePageId, widgetBulkUpdate, t]);

  // Handle widget modal form submission (add or edit)
  const handleSubmitWidgetModal = useCallback(
    async (values: Values) => {
      setWidgetModalLoading(true);
      try {
        if (editingWidget) {
          // Edit mode - update existing widget
          const widgetId = editingWidget.id || editingWidget.tempId || '';
          updateWidget(widgetId, values);
        } else {
          // Add mode - create new widget
          addWidget(values);
        }
        handleCloseWidgetModal();
      } finally {
        setWidgetModalLoading(false);
      }
    },
    [editingWidget, updateWidget, addWidget, handleCloseWidgetModal],
  );

  // Handle cancel edit mode
  // Only navigate - DO NOT clear local state here!
  // Navigation is async (router), but state clearing is sync.
  // If we clear state while still in edit mode, we get a flash of empty state.
  // Local state will be overwritten on next edit mode entry via initializeLocalWidgets.
  const handleCancelEdit = useCallback(() => {
    onExitEditMode();
  }, [onExitEditMode]);

  // Handle save view
  const handleSaveView = useCallback(async () => {
    // If no widgets, just exit edit mode without API call
    if (localWidgets.length === 0) {
      onExitEditMode();
      // Don't clear local state here - it will be cleared on next edit mode entry
      return;
    }

    const success = await saveChanges();
    if (success) {
      // Wait for refetch to complete before exiting edit mode
      // This prevents the grid from showing old data during the transition
      await refetchWidgets();
      // Exit edit mode - this switches Widgets to use 'widgets' prop
      onExitEditMode();
      // Don't clear local state here - it would cause a flash since isEditMode
      // state update is asynchronous. Local state will be cleared on next edit mode entry
      // via initializeLocalWidgets which overwrites localWidgets with fresh data.
    }
  }, [localWidgets.length, saveChanges, refetchWidgets, onExitEditMode]);

  return {
    localWidgets,
    localGrid,
    setLocalWidgets,
    setLocalGrid,
    saveLoading,
    // Widget modal state (data only, open/close managed by useModalManagement)
    editingWidget,
    widgetModalLoading,
    // Widget modal handlers
    handleOpenWidgetModal,
    handleCloseWidgetModal,
    handleEditWidget,
    handleSubmitWidgetModal,
    // Widget actions
    handleRemoveWidget,
    // Layout
    initializeLocalWidgets,
    updateLayout,
    // Edit mode actions
    handleCancelEdit,
    handleSaveView,
  };
}
